import { SKI_LOCATIONS, SURF_LOCATIONS } from "@/lib/conditions/locations";
import { SAILING_LOCATIONS } from "@/lib/conditions/sailing-scoring";
import {
  CALL_SPORT_LABELS,
  canonicalCallSportsForAccess,
  type CallSport,
} from "@/lib/the-call/types";
import { findTodayRegion } from "@/lib/today/regions";

const COMPANION_SIGNUP_INTENT_KEY = "pw_companion_intent";
const COMPANION_SIGNUP_INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const COMPANION_SIGNUP_INTENT_MAX_URL_LENGTH = 2048;
export const COMPANION_SIGNUP_INTENT_QUERY_PARAM = "setup";

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;
type CompanionSignupIntentSpotType = "ski" | "surf" | "sailing";

export interface CompanionSignupIntent {
  v: 1;
  createdAt: string;
  regionSlug?: string;
  spotSlugs?: string[];
  sports?: string[];
  autoSaveSpots?: true;
}

export interface CompanionSignupIntentPrefill {
  region: {
    slug: string;
    name: string;
    anchorSlug: string;
  } | null;
  suggestedSpots: {
    slug: string;
    name: string;
    spotType: CompanionSignupIntentSpotType;
  }[];
  suggestedSports: {
    sport: CallSport;
    label: string;
  }[];
  autoSaveSpots: boolean;
}

export interface BuildCompanionSignupIntentInput {
  now?: Date | string;
  regionSlug?: string | null;
  spotSlugs?: string[] | null;
  sports?: string[] | null;
  autoSaveSpots?: boolean;
}

export function buildConditionSaveSignupIntentInput(options: {
  spotSlug: string;
  sport?: string | null;
}): BuildCompanionSignupIntentInput {
  const sport = typeof options.sport === "string" && options.sport.trim()
    ? options.sport
    : undefined;
  return {
    spotSlugs: [options.spotSlug],
    sports: sport ? [sport] : undefined,
    autoSaveSpots: true,
  };
}

function compactUniqueStrings(values: string[] | null | undefined): string[] | undefined {
  if (!Array.isArray(values)) return undefined;

  const deduped = Array.from(
    new Set(
      values
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  );

  return deduped.length > 0 ? deduped : undefined;
}

function normaliseRegionSlug(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseCreatedAt(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function resolveNow(now: Date | string | undefined): Date {
  if (now instanceof Date) return now;
  return now ? new Date(now) : new Date();
}

export function buildCompanionSignupIntent(
  input: BuildCompanionSignupIntentInput,
): CompanionSignupIntent {
  const intent: CompanionSignupIntent = {
    v: 1,
    createdAt: resolveNow(input.now).toISOString(),
  };

  const regionSlug = normaliseRegionSlug(input.regionSlug);
  const spotSlugs = compactUniqueStrings(input.spotSlugs);
  const sports = compactUniqueStrings(input.sports);

  if (regionSlug) intent.regionSlug = regionSlug;
  if (spotSlugs) intent.spotSlugs = spotSlugs;
  if (sports) intent.sports = sports;
  if (input.autoSaveSpots === true && spotSlugs) intent.autoSaveSpots = true;

  return intent;
}

export function parseCompanionSignupIntent(
  raw: string | null | undefined,
  options: { now?: Date | string } = {},
): CompanionSignupIntent | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      parsed.v !== 1 ||
      Object.prototype.hasOwnProperty.call(parsed, "baseKind")
    ) {
      return null;
    }

    const createdAt = parseCreatedAt(parsed.createdAt);
    if (!createdAt) return null;

    const age =
      resolveNow(options.now).getTime() - new Date(createdAt).getTime();
    if (age < 0 || age > COMPANION_SIGNUP_INTENT_MAX_AGE_MS) {
      return null;
    }

    return buildCompanionSignupIntent({
      now: createdAt,
      regionSlug:
        typeof parsed.regionSlug === "string" ? parsed.regionSlug : undefined,
      spotSlugs: Array.isArray(parsed.spotSlugs)
        ? parsed.spotSlugs.filter((value): value is string => typeof value === "string")
        : undefined,
      sports: Array.isArray(parsed.sports)
        ? parsed.sports.filter((value): value is string => typeof value === "string")
        : undefined,
      autoSaveSpots: parsed.autoSaveSpots === true,
    });
  } catch {
    return null;
  }
}

export function parseCompanionSignupIntentParam(
  value: string | null | undefined,
  options: { now?: Date | string } = {},
): CompanionSignupIntent | null {
  if (!value || value.length > COMPANION_SIGNUP_INTENT_MAX_URL_LENGTH) {
    return null;
  }

  try {
    return parseCompanionSignupIntent(value, options);
  } catch {
    return null;
  }
}

export function buildCompanionSignupNext(
  input: BuildCompanionSignupIntentInput,
): string {
  return companionSignupNextForIntent(buildCompanionSignupIntent(input));
}

export function buildCompanionSignupNextFromParam(
  value: string | null | undefined,
  options: { now?: Date | string } = {},
): string {
  const intent = parseCompanionSignupIntentParam(value, options);
  return intent ? companionSignupNextForIntent(intent) : "/companion";
}

export function buildCompanionSignupHref(
  input: BuildCompanionSignupIntentInput,
): string {
  return `/signup?${new URLSearchParams({
    next: buildCompanionSignupNext(input),
  }).toString()}`;
}

export function resolveCompanionSignupIntentPrefill(
  intent: CompanionSignupIntent | null,
  options: { includeNamedSupProfiles?: boolean } = {},
): CompanionSignupIntentPrefill {
  const region = intent?.regionSlug ? findTodayRegion(intent.regionSlug) : null;
  const spotSlugs = compactUniqueStrings(intent?.spotSlugs) ?? [];
  const sports = compactUniqueStrings(intent?.sports) ?? [];

  return {
    region: region
      ? {
          slug: region.slug,
          name: region.name,
          anchorSlug: region.locationSlugs[0] ?? "",
        }
      : null,
    suggestedSpots: spotSlugs.flatMap((slug) => {
      const spot = SIGNUP_INTENT_SPOTS.get(slug);
      return spot ? [spot] : [];
    }),
    suggestedSports: canonicalCallSportsForAccess(
      sports,
      options.includeNamedSupProfiles === true,
    ).map((sport) => ({
      sport,
      label: CALL_SPORT_LABELS[sport],
    })),
    autoSaveSpots: intent?.autoSaveSpots === true,
  };
}

export function writeCompanionSignupIntent(
  input: BuildCompanionSignupIntentInput,
  storage: StorageLike = window.localStorage,
): CompanionSignupIntent | null {
  try {
    const intent = buildCompanionSignupIntent(input);
    if (!hasCompanionSignupIntentPayload(intent)) {
      clearCompanionSignupIntent(storage);
      return null;
    }
    storage.setItem(COMPANION_SIGNUP_INTENT_KEY, JSON.stringify(intent));
    return intent;
  } catch {
    return null;
  }
}

export function readCompanionSignupIntent(
  storage: StorageLike = window.localStorage,
  options: { now?: Date | string } = {},
): CompanionSignupIntent | null {
  try {
    const raw = storage.getItem(COMPANION_SIGNUP_INTENT_KEY);
    const intent = parseCompanionSignupIntent(raw, options);
    if (raw !== null && !intent) clearCompanionSignupIntent(storage);
    return intent;
  } catch {
    return null;
  }
}

export function clearCompanionSignupIntent(
  storage: StorageLike = window.localStorage,
): void {
  try {
    storage.removeItem(COMPANION_SIGNUP_INTENT_KEY);
  } catch {
    // ignore
  }
}

const SIGNUP_INTENT_SPOTS: Map<
  string,
  CompanionSignupIntentPrefill["suggestedSpots"][number]
> = new Map([
  ...SKI_LOCATIONS.map((location) =>
    signupIntentSpotEntry(location.id, location.name, "ski"),
  ),
  ...SURF_LOCATIONS.map((location) =>
    signupIntentSpotEntry(location.id, location.name, "surf"),
  ),
  ...SAILING_LOCATIONS.map((location) =>
    signupIntentSpotEntry(location.slug, location.name, "sailing"),
  ),
]);

function signupIntentSpotEntry(
  slug: string,
  name: string,
  spotType: CompanionSignupIntentSpotType,
): [string, CompanionSignupIntentPrefill["suggestedSpots"][number]] {
  return [
    slug,
    {
      slug,
      name,
      spotType,
    },
  ];
}

function companionSignupNextForIntent(intent: CompanionSignupIntent): string {
  if (!hasCompanionSignupIntentPayload(intent)) return "/companion";

  return `/companion?${new URLSearchParams({
    [COMPANION_SIGNUP_INTENT_QUERY_PARAM]: JSON.stringify(intent),
  }).toString()}`;
}

function hasCompanionSignupIntentPayload(
  intent: CompanionSignupIntent,
): boolean {
  return Boolean(
    intent.regionSlug ||
      intent.spotSlugs?.length ||
      intent.sports?.length,
  );
}
